import { useEffect, useRef } from "react";

import { useConsensusSignals } from "../hooks/useConsensus";
import { useDecidedSignals } from "../hooks/useDecided";
import { useFamilySignals } from "../hooks/useFamily";
import { useMispricedSignals } from "../hooks/useMispriced";
import { useNow } from "../hooks/useNow";
import { useStatus } from "../hooks/useStatus";
import { chime } from "../lib/chime";
import { strategyLabel } from "../lib/strategies";
import {
  strategyAlerts,
  unannouncedAlerts,
  type StrategyAlert,
  type StrategyFeed,
} from "../lib/strategyalerts";
import { useSignalSound } from "../store/useSignalSound";
import { StrategyRowActions } from "./StrategyRowActions";

/**
 * Полоса алертов по строкам стратегий — ступень `alert` трёх подсистем с портом
 * постановки.
 *
 * # Почему в макете, а не на странице стратегии
 *
 * Тот же довод, что у карточек китов: окна серии закрываются каждые пять минут,
 * а строка рождается за 17–46 секунд до конца своего окна. Показанная только
 * тому, кто открыл нужную панель, карточка не показалась бы почти никогда — и
 * ступень `alert`, ради которой веха и делалась, работала бы вхолостую.
 *
 * # Почему она заняла место карточек замка
 *
 * Полоса сквозная и висит поверх всех страниц: две полосы предложений сделок
 * соревновались бы за один взгляд, а звонок у них был бы неразличим на слух.
 * Замок остаётся подсистемой и остаётся в «Сверке индекса»; здесь у него больше
 * нет карточки и звонка — это решение оператора, а не устройство системы, и
 * возвращается оно одной строкой в App.tsx.
 *
 * # Почему кнопки взяты у строки ленты
 *
 * StrategyRowActions целиком: команда, сверка суммы, ожидание вердикта по
 * trace_id и рубеж «ответа нет» — это одно поведение, и вторая его копия в
 * карточке разошлась бы с первой на первой же правке. Карточка добавляет к
 * строке ровно две вещи: она видна с любой страницы и она звучит.
 */
export function StrategyAlerts() {
  const status = useStatus();
  // Час — с запасом: карточка живёт минуты, а лента и без того опрашивается
  // тактом в три секунды. Меньший срок сузил бы ответ, ничего не ускорив.
  const decided = useDecidedSignals(1, 40);
  const family = useFamilySignals(1, 40);
  const mispriced = useMispricedSignals(1, 40);
  const consensus = useConsensusSignals(1, 40);
  // Секундный тик: обратный отсчёт обязан идти сам, без нового сообщения.
  const now = useNow(1000);

  const byID = (id: string) => status.data?.strategies?.find((s) => s.id === id);
  const feeds: StrategyFeed[] = [
    { strategy: "decided", status: byID("decided"), rows: decided.data?.signals ?? [] },
    { strategy: "family", status: byID("family"), rows: family.data?.signals ?? [] },
    {
      strategy: "mispriced",
      status: byID("mispriced"),
      // ТОЛЬКО тейкерские строки, и отбор стоит здесь, а не в клетке.
      //
      // У расхождения живой путь открыт одному тейкеру (ADR-0022): мейкерская
      // строка получает живое правило `role` и ордером не станет ни при каком
      // нажатии. Карточка по ней предлагала бы сделку, которую трейдер отвергнет,
      // — то есть учила бы оператора, что половина кнопок ненастоящие.
      //
      // Клетка этого не закрывает: она из ОДНОГО источника, а роль — вторая ось,
      // и складывать их в строку ноты значило бы завести клетку, которой у
      // подсистемы нет.
      rows: (mispriced.data?.signals ?? []).filter((r) => r.role === "taker"),
    },
    {
      strategy: "consensus",
      status: byID("consensus"),
      // Фильтра по роли здесь НЕТ, и это не пропуск: у котировки строка ОДНА на
      // клетку и несёт обе гипотезы сразу, а живьём берётся тейкерская. Отбирать
      // нечего — роль решает не строку, а выбор внутри неё.
      //
      // Клетку же отбирает inCell по ноте consensus_live_cell: она из трёх осей
      // («6500-1500-2»), и по одному окну строк рождается до восемнадцати.
      rows: consensus.data?.signals ?? [],
    },
  ];

  const live = strategyAlerts(feeds, now);
  useChime(live);

  if (live.length === 0) return null;

  return (
    <div className="border-b border-[var(--color-border)] bg-[var(--color-panel)]">
      <div className="mx-auto flex max-w-[1600px] flex-col gap-2 px-4 py-2">
        {live.map((a) => (
          <AlertCard key={a.key} alert={a} now={now} />
        ))}
      </div>
    </div>
  );
}

/**
 * Звонок по НЕПРОЗВУЧАВШИМ карточкам.
 *
 * Выключенный звук всё равно ПОМЕЧАЕТ карточки прозвучавшими: иначе включение
 * звука дало бы залп по всему, что накопилось за смену. Тот же приём и та же
 * причина, что у карточек китов и замка.
 */
function useChime(live: readonly StrategyAlert[]) {
  const on = useSignalSound((s) => s.on);
  const announced = useRef<Set<string>>(new Set());

  useEffect(() => {
    const fresh = unannouncedAlerts(live, announced.current);
    for (const key of fresh) announced.current.add(key);
    // Один звонок на пачку: две карточки, приехавшие в один тик, — это одно
    // событие для уха, а два наложенных тона слышны как дребезг.
    if (on && fresh.length > 0) chime();
  }, [live, on]);
}

/**
 * Одна карточка: что за окно, сколько осталось и кнопки.
 *
 * Обратный отсчёт идёт до конца ОКНА, а не до собственного срока карточки: за
 * концом окна вход обосновать нечем, и трейдер снимет заявку свипером. Ровно то
 * же число, по которому кнопка гаснет.
 */
function AlertCard({ alert, now }: { alert: StrategyAlert; now: number }) {
  const left = Math.max(0, Math.ceil((Date.parse(alert.windowEnd) - now) / 1000));

  return (
    <div className="flex flex-wrap items-center gap-3 rounded border border-[var(--color-accent)] px-3 py-2 text-xs">
      <span className="font-semibold text-[var(--color-accent)]">
        {strategyLabel(alert.strategy)}
      </span>
      {alert.asset && <span className="font-semibold">{alert.asset}</span>}
      {alert.cell && <span className="text-[var(--color-muted)]">{alert.cell}</span>}
      {/* Секунды до конца окна — часть решения, а не украшение: на десяти
          оставшихся секундах мейкерская заявка почти наверняка не наберётся. */}
      <span className={left <= 10 ? "text-[var(--color-sell)]" : "text-[var(--color-muted)]"}>
        {left} с
      </span>
      <StrategyRowActions
        strategy={alert.strategy}
        signalID={alert.id}
        windowEnd={alert.windowEnd}
      />
    </div>
  );
}
